import React, { useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'

import { UpcomingMeetingContainerV2 } from 'pages/Clients/container'
import {
  RequestMeeting,
  TextButtonWrapper,
  ToastWrapper,
} from '../../../../components/Calendar/style'
import {
  UserProfile,
  useGetTackleMeetingsQuery,
} from '../../../../__generated__/api-types-and-hooks'
import {
  CreateEventContainer,
  ScheduleMeetingModalBoxContainer,
} from '../../../../pages/Owner/container'

import { MEETING_STEPPER, ROLES } from 'config'
import { useAuthentication } from 'utils/authentication'
import { useFeatureFlag } from 'hooks/userFeatureFlag'
import ModalBox from 'components/Common/ModalBox'
import { ISelectedClient } from '../../../../components/Calendar/MeetingSchedule'
import { getTenantId } from 'utils/getTenantId'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'App'
import { getTackleMeetingEventsAction } from 'store/actions/clients'

import { useFlashParams } from 'hooks/useFlashParams'
import MeetingFailedInvitesToast from 'components/Calendar/Meetings/Toasts/MeetingFailedInvitesToast'
import Toast from 'components/Common/Toast'
import MeetingUpdateFailedToast from 'components/Calendar/Meetings/Toasts/MeetingUpdateFailedToast'
import MeetingCancelIcon from 'components/Common/SvgIcons/MeetingCancelIcon'
import { updateMeetingStepperAction } from 'store/actions/calendar'
import { getRoleFromPath } from 'utils/helper'

interface IMeetingProps {
  user: UserProfile
  upComingMeeting: any[]
  previousMeeting: number[]
  oAuthUrl: string
  getUserConnectorsAction: any
  resetOAuthUrlAction: any
  meetingStepper: MEETING_STEPPER
}

const Meeting = ({
  user,
  meetingStepper,
  getUserConnectorsAction,
  oAuthUrl,
  resetOAuthUrlAction,
}: IMeetingProps) => {
  let { clientId } = useParams()
  const dispatch = useDispatch()
  const tenantId = getTenantId()
  const [searchQuery, setSearchQuery] = useState('')
  const [showUpComingMeeting, setShowUpComingMeeting] = useState('upcoming')
  const [open, setOpen] = useState(false)
  const [showModalBox, setShowModalBox] = useState(false)
  const [status, setStatus] = useState('open')

  const { flashParams, removeFlashParams } = useFlashParams<{
    meetingId?: string
    failedEmails?: string
    meetingCreationStatus?: 'success' | 'failed'
    meetingEditStatus?: 'success' | 'failed'
    toastVisibilityTimeout?: string
    toastMessage?: string
    toastVariant?: 'success' | 'alert'
    connectorName: string
    calendarDisconnectedError: string
    disconnectedTools: string
  } | null>()

  const [showRequestAMeetingModalBox, setShowRequestAMeetingModalBox] = useState<boolean>(false)
  const handleCallback = (data) => {
    setStatus(data)
  }

  const handleCallbackBadge = (data) => {
    if (data === 'upcoming') {
      refetchUpcomingData()
    } else if (data === 'previous') {
      refetchPreviousData()
    } else if (data === 'cancelled') {
      refetchCanceledData()
    }
  }
  useAuthentication(oAuthUrl, getUserConnectorsAction, resetOAuthUrlAction)
  const advisorMeetingRequestButtonEnabled = useFeatureFlag(
    user?.id,
    'release-advisor-schedule-meeting-rollout'
  )

  const isUserBusinessOwner = (): boolean => {
    const roles = user?.roles ?? []
    return roles[0] === 'BUSINESS_OWNER'
  }
  const isUserBSOUser = (): boolean => {
    const roles = user?.roles ?? []
    return roles.includes('BSO_ADVISOR') || roles.includes('BSO_ADMIN')
  }

  const handleAlgoliaSearch = async (query: string) => {
    setSearchQuery(query)
  }

  const handleClose = () => {
    setShowModalBox(false)
    setSelectedAdvisorId(null)
    setSelectedClient(null)
    setShowRequestAMeetingModalBox(false)
    dispatch(updateMeetingStepperAction({ actionType: 'reset', stepName: MEETING_STEPPER.ADVISOR }))
  }

  const [selectedAdvisorId, setSelectedAdvisorId] = useState<null | string>(null)
  const [selectedClient, setSelectedClient] = useState<ISelectedClient | null | undefined>(null)
  const [selectedDuration, setSelectedDuration] = useState(60)

  const clientID = useSelector((state: RootState) => state.user.user.id)
  const { isMeetingModalOpen, isScheduled } = useSelector((state: RootState) => state.calendar)

  const role = getRoleFromPath()
  const location = useLocation()
  const history = useHistory()

  // Ensure default filter=upcoming on initial load if not present
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const filter = params.get('filter')
    if (!filter) {
      params.set('filter', 'upcoming')
      const searchStr = params.toString()
      history.replace(`${location.pathname}${searchStr ? `?${searchStr}` : ''}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync tab with URL on initial load and when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const filter = params.get('filter')
    if (filter === 'past' && showUpComingMeeting !== 'previous') {
      setShowUpComingMeeting('previous')
      setSearchQuery('')
    } else if (!filter && showUpComingMeeting === 'previous') {
      setShowUpComingMeeting('upcoming')
      setSearchQuery('')
    }
  }, [location.search, showUpComingMeeting])

  const handleTabChange = (tab) => {
    setShowUpComingMeeting(tab)
    setSearchQuery('') // Clear search when tab changes

    // Update URL query param when switching tabs
    const params = new URLSearchParams(location.search)
    if (tab === 'previous') {
      params.set('filter', 'past')
    } else if (tab === 'upcoming') {
      params.set('filter', 'upcoming')
    } else if (tab === 'cancelled') {
      params.set('filter', 'cancelled')
    } else {
      params.delete('filter')
    }
    const searchStr = params.toString()
    history.push(`${location.pathname}${searchStr ? `?${searchStr}` : ''}`)
  }

  const { data: upcoming, refetch: refetchUpcomingData } = useGetTackleMeetingsQuery({
    input: {
      ownerId: clientId ?? clientID,
      filter: 'upcoming',
      tenantId,
    },
  })

  const { data: previous, refetch: refetchPreviousData } = useGetTackleMeetingsQuery({
    input: {
      ownerId: clientId ?? clientID,
      filter: 'previous',
      tenantId,
    },
  })
  const { data: canceled, refetch: refetchCanceledData } = useGetTackleMeetingsQuery({
    input: {
      ownerId: clientId ?? clientID,
      filter: 'cancelled',
      tenantId,
    },
  })

  const handleMeetingActionSuccess = useCallback(() => {
    refetchUpcomingData()
    refetchPreviousData()
    refetchCanceledData()
  }, [refetchUpcomingData, refetchPreviousData, refetchCanceledData])

  useEffect(() => {
    if (isScheduled || (isMeetingModalOpen && role !== ROLES.BUSINESS_OWNER)) {
      setShowRequestAMeetingModalBox(false)
      if (showUpComingMeeting === 'upcoming') {
        dispatch(
          getTackleMeetingEventsAction.STARTED({
            filter: 'upcoming',
            ownerId: clientId ?? clientID,
          })
        )
      }
      if (showUpComingMeeting === 'previous') {
        dispatch(
          getTackleMeetingEventsAction.STARTED({
            filter: 'previous',
            ownerId: clientId ?? clientID,
          })
        )
      }
      if (showUpComingMeeting === 'cancelled') {
        dispatch(
          getTackleMeetingEventsAction.STARTED({
            filter: 'cancelled',
            ownerId: clientId ?? clientID,
          })
        )
      }

      refetchUpcomingData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMeetingModalOpen, isScheduled, role, clientId, clientID, refetchUpcomingData, dispatch])

  const handleCloseMeetingToast = useCallback(() => {
    removeFlashParams()
  }, [removeFlashParams])

  useEffect(() => {
    if (flashParams?.toastVisibilityTimeout) {
      const timeout = setTimeout(() => {
        handleCloseMeetingToast()
      }, Number(flashParams.toastVisibilityTimeout))

      return () => clearTimeout(timeout)
    }
    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flashParams?.toastVisibilityTimeout, handleCloseMeetingToast])

  const onCloseHandler = () => {
    setShowModalBox(false)
    setSelectedAdvisorId(null)
    setShowRequestAMeetingModalBox(false)
    setSelectedDuration(60)
    dispatch(updateMeetingStepperAction({ actionType: 'reset', stepName: MEETING_STEPPER.ADVISOR }))
  }

  return (
    <>
      <div className="xs:py-11 md:p-6 mdl:px-6 mdl:py-0 min-h-[600px] w-full">
        {flashParams?.failedEmails?.split(',')?.length && (
          <ToastWrapper>
            <MeetingFailedInvitesToast
              onClose={handleCloseMeetingToast}
              failedEmails={flashParams?.failedEmails ?? ''}
              lastCreatedMeetingId={flashParams.meetingId ?? ''}
            />
          </ToastWrapper>
        )}
        {(flashParams?.meetingCreationStatus || flashParams?.meetingEditStatus === 'success') &&
          !flashParams?.failedEmails?.split(',')?.length && (
            <ToastWrapper>
              <Toast
                variant={flashParams?.toastVariant}
                showCloseButton={true}
                onClose={handleCloseMeetingToast}
              >
                {flashParams?.toastMessage}
              </Toast>
            </ToastWrapper>
          )}
        {flashParams?.meetingEditStatus === 'failed' && (
          <ToastWrapper>
            <MeetingUpdateFailedToast
              onClose={handleCloseMeetingToast}
              meetingId={flashParams.meetingId ?? ''}
            />
          </ToastWrapper>
        )}

        {status.includes('open') && (
          <>
            <div className="mb-6 xs:px-5 md:px-0 mt-6">
              <h4
                className="flex items-center text-4xl font-medium mb-3"
                data-testid="meeting-record-title"
              >
                Meeting Record
              </h4>
              <p className="text-[#5E5E5E]">
                Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8 xs:px-5 md:px-0 grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 ">
              <div className="border-[#DBDBDB] border rounded-lg flex items-center w-full px-3 py-2">
                <img
                  alt=""
                  className="w-6 cursor-pointer "
                  onClick={() => setOpen(!open)}
                  src={require('../../../../appAssets/appImages/search.png')}
                />
                <input
                  data-testid="meeting-search"
                  value={searchQuery}
                  onChange={(e) => handleAlgoliaSearch(e.target.value)}
                  placeholder="Search by name..."
                  className="w-full text-sm px-3 py-1 border-0 rounded focus:border-none focus:outline-none focus:ring-0"
                />
              </div>

              <div className="flex items-center gap-4">
                <div className="flex-1 flex items-center justify-center">
                  <div className="w-auto inline-block rounded-lg p-1 py-1  bg-[#F5F5F5]">
                    <ul className="flex flex-nowrap gap-2 md:gap-4 text-sm font-medium list-none pl-0">
                      <li>
                        <div
                          data-testid="tab-upcoming"
                          className={
                            showUpComingMeeting === 'upcoming'
                              ? 'px-4 py-2 bg-white rounded-lg text-xs cursor-pointer drop-shadow-sm min-w-[125px] text-center font-[500] whitespace-nowrap'
                              : 'px-4 py-2 bg-gray-200 rounded text-xs cursor-pointer min-w-[125px] text-center font-[400] whitespace-nowrap'
                          }
                          onClick={() => handleTabChange('upcoming')}
                        >
                          Upcoming{' '}
                          <span
                            data-testid="tab-upcoming-count"
                            className={
                              showUpComingMeeting === 'upcoming'
                                ? 'bg-[#705AF8] ml-1 w-6 inline-block text-center py-1 rounded-md text-white'
                                : 'ml-1 bg-[#ECECEC] w-6 inline-block text-center py-1 rounded-md text-[#5E5E5E]'
                            }
                          >
                            {upcoming?.getTackleMeetings?.data?.length || '0'}
                          </span>
                        </div>
                      </li>

                      <li>
                        <div
                          data-testid="tab-previous"
                          className={
                            showUpComingMeeting === 'previous'
                              ? 'px-4 py-2 bg-white rounded-lg text-xs cursor-pointer drop-shadow-sm  text-center font-[500] whitespace-nowrap'
                              : 'px-4 py-2 bg-gray-200 text-[#5E5E5E] rounded text-xs cursor-pointer  text-center font-[400] whitespace-nowrap'
                          }
                          onClick={() => handleTabChange('previous')}
                        >
                          Past{' '}
                          <span
                            data-testid="tab-previous-count"
                            className={
                              showUpComingMeeting === 'previous'
                                ? 'bg-[#705AF8] ml-1 w-6 inline-block text-center py-1 rounded-md text-white'
                                : 'ml-1 bg-[#ECECEC] w-6 py-1 rounded-md text-[#5E5E5E] inline-block text-center'
                            }
                          >
                            {previous?.getTackleMeetings?.data?.length || '0'}
                          </span>
                        </div>
                      </li>
                      <li>
                        <div
                          data-testid="tab-canceled"
                          className={
                            showUpComingMeeting === 'cancelled'
                              ? 'px-4 py-2 bg-white rounded-lg text-xs cursor-pointer drop-shadow-sm min-w-[125px] text-center font-[500] whitespace-nowrap'
                              : 'px-4 py-2 bg-gray-200 text-[#5E5E5E] rounded text-xs cursor-pointer min-w-[125px] text-center font-[400] whitespace-nowrap'
                          }
                          onClick={() => handleTabChange('cancelled')}
                        >
                          Canceled{' '}
                          <span
                            data-testid="tab-canceled-count"
                            className={
                              showUpComingMeeting === 'cancelled'
                                ? 'bg-[#705AF8] ml-1 w-6 inline-block text-center py-1 rounded-md text-white'
                                : 'ml-1 bg-[#ECECEC] w-6 py-1 rounded-md text-[#5E5E5E] inline-block text-center'
                            }
                          >
                            {canceled?.getTackleMeetings?.data?.length || '0'}
                          </span>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <RequestMeeting className="sm:py-0 pr-0 md:-mr-2 ">
                    {advisorMeetingRequestButtonEnabled && (
                      <TextButtonWrapper>
                        <button
                          data-testid="create-appointment-btn"
                          type="button"
                          onClick={() => {
                            if (isUserBusinessOwner()) {
                              setShowRequestAMeetingModalBox(true)
                            } else {
                              setShowModalBox(true)
                            }
                          }}
                          className="flex items-center gap-2 px-4 py-3 bg-purple text-white rounded-lg"
                        >
                          Create an Appointment{' '}
                          <img
                            src={require('../../../../appAssets/appImages/plus-small.png')}
                            alt=""
                            className="w-6 h-6"
                          />
                        </button>
                      </TextButtonWrapper>
                    )}
                  </RequestMeeting>

                  {showModalBox ? (
                    <ModalBox
                      data-testid="modal-box"
                      height={'xs:h-full sm:h-auto max-h-full'}
                      margin="sm:mx-5"
                      onClose={handleClose}
                      title={MEETING_STEPPER.ADVISOR}
                      onBack={
                        meetingStepper === MEETING_STEPPER.ADVISOR ||
                        meetingStepper === MEETING_STEPPER.MEETING_CONFIRMATION
                          ? undefined
                          : () => dispatch(updateMeetingStepperAction({ actionType: 'decrement' }))
                      }
                      Icon={MeetingCancelIcon}
                    >
                      <ScheduleMeetingModalBoxContainer
                        setSelectedAdvisorId={setSelectedAdvisorId}
                        selectedAdvisorId={selectedAdvisorId}
                        setShowModalBox={setShowModalBox}
                        selectedClient={selectedClient}
                        setSelectedClient={setSelectedClient}
                        isBSOUser={isUserBSOUser()}
                        setSelectedDuration={setSelectedDuration}
                        selectedDuration={selectedDuration}
                      />
                    </ModalBox>
                  ) : (
                    showModalBox &&
                    isUserBSOUser() && (
                      <div>
                        <ModalBox
                          title="Meeting Settings"
                          onClose={onCloseHandler}
                          height="h-full"
                          padding="xs:p-6 sm:p-7"
                          margin="sm:py-4"
                        >
                          <CreateEventContainer onClose={setShowModalBox} />
                        </ModalBox>
                      </div>
                    )
                  )}

                  {showRequestAMeetingModalBox && (
                    <div>
                      <ModalBox
                        data-testid="modal-box"
                        title={MEETING_STEPPER.ADVISOR}
                        onClose={handleClose}
                        height="xs:h-screen sm:h-auto"
                        position="top"
                        onBack={
                          meetingStepper === MEETING_STEPPER.ADVISOR
                            ? undefined
                            : () =>
                                dispatch(updateMeetingStepperAction({ actionType: 'decrement' }))
                        }
                      >
                        <ScheduleMeetingModalBoxContainer
                          setSelectedAdvisorId={setSelectedAdvisorId}
                          selectedAdvisorId={selectedAdvisorId}
                          setShowModalBox={setShowRequestAMeetingModalBox}
                          selectedClient={selectedClient}
                          setSelectedClient={setSelectedClient}
                          selectedDuration={selectedDuration}
                          setSelectedDuration={setSelectedDuration}
                          isBSOUser={isUserBusinessOwner()}
                        />
                      </ModalBox>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        <div data-testid="upcoming-meeting-list">
          <UpcomingMeetingContainerV2
            filter={`${showUpComingMeeting} `}
            status={handleCallback}
            searchResults={searchQuery || undefined}
            badge={handleCallbackBadge}
            onMeetingActionSuccess={handleMeetingActionSuccess}
          />
        </div>
      </div>
    </>
  )
}

export default Meeting
